import React from "react";

/* ─── Shared helpers ──────────────────────────────────── */
function toTitleCase(str) {
  if (!str) return "—";
  return str.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export function parseCfg(raw) {
  if (!raw) return {};
  if (typeof raw === "string") { try { return JSON.parse(raw); } catch { return {}; } }
  return raw;
}

export function gradeFor(pct) {
  if (pct >= 91) return { g: "A1", p: "Outstanding",   color: "#15803D" };
  if (pct >= 81) return { g: "A2", p: "Excellent",     color: "#16A34A" };
  if (pct >= 71) return { g: "B1", p: "Very Good",     color: "#2563EB" };
  if (pct >= 61) return { g: "B2", p: "Good",          color: "#3B82F6" };
  if (pct >= 51) return { g: "C1", p: "Average",       color: "#D97706" };
  if (pct >= 41) return { g: "C2", p: "Satisfactory",  color: "#EA580C" };
  if (pct >= 33) return { g: "D",  p: "Pass",          color: "#B45309" };
  return           { g: "E",  p: "Needs Improvement", color: "#DC2626" };
}

export const SAMPLE_MARKS = {
  "Mathematics":    [78, 82, 75, 88],
  "Science":        [65, 70, 68, 74],
  "English":        [82, 85, 80, 90],
  "Hindi":          [70, 72, 68, 76],
  "Social Science": [74, 78, 72, 80],
  "Computer Sc.":   [88, 90, 85, 92],
  "Drawing/Art":    [92, 94, 90, 95],
};

const DEFAULT_COSCHO = ["Work Education", "Art Education", "Health & Physical Education", "Discipline"];
export const COSCHO_GRADES = ["A", "A+", "B+", "A", "B", "A"];
export const BAR_COLORS    = ["#1D4ED8", "#2196F3", "#B45309", "#B91C1C"];

/* ─── Data source builder ─────────────────────────────── */
function buildDataSrc(cfg, printData) {
  if (!printData) {
    // Sample data (for preview)
    const scholasticSubs = Object.keys(SAMPLE_MARKS);
    return {
      schoolName: "Greenfield Public School",
      schoolAddress: "123 Knowledge Road, Education City – 400001",
      schoolPhone: "022-12345678",
      schoolEmail: "info@greenfield.edu.in",
      schoolExtra: "Affiliated to CBSE, New Delhi  |  Affiliation No. 1230012  |  School Code: 98765",
      schoolLogo: null,
      studentName: "Rohan Kumar",
      fatherName: "Ramesh Kumar",
      motherName: "Sunita Kumar",
      admNo: "GPS/2024/0814",
      className: "VIII – A",
      rollNo: "14",
      dob: "15 Aug 2010",
      session: "2024–25",
      examTitle: null, // null means use tpl.name
      remark: "Rohan has shown consistent improvement. Needs to focus more on Science. Excellent in Computer Science.",
      subjects: scholasticSubs,
      getObtained: (sub, examIdx) => SAMPLE_MARKS[sub]?.[examIdx] ?? 75,
      getMaxMarks: () => 100,
      passMarks: () => 33,
      isAbsent: () => false,
      totalObtained: scholasticSubs.reduce((s, sub) => s + (SAMPLE_MARKS[sub]?.[0] || 0), 0),
      totalMax: scholasticSubs.length * 100,
      percentage: 79,
      grade: "B1",
      overallPass: true,
      rank: 3,
      coscho: cfg.coScholasticActivities || DEFAULT_COSCHO,
      getCoSchoMark: () => null,
      getCoSchoGrade: (idx) => COSCHO_GRADES[idx],
      attendance: { workingDays: 240, daysPresent: 220 },
      gradeFor,
      gradeScale: null,
    };
  }

  const { school, student, exam, result } = printData;
  const scholastic = result.subjects.filter(s => s.category === "scholastic");
  const coSch = result.subjects.filter(s => s.category === "co_scholastic");

  // Build grade function — uses school's custom scale if configured
  const rawScale = printData?.gradeScale; // array of { minPct, grade, desc, color }, sorted high→low
  const gradeForFn = (rawScale && rawScale.length > 0)
    ? (pct) => {
        for (const band of rawScale) {
          if (pct >= band.minPct) return { g: band.grade, p: band.desc, color: band.color };
        }
        const last = rawScale[rawScale.length - 1];
        return { g: last.grade, p: last.desc, color: last.color };
      }
    : gradeFor;

  const cls = student.ClassSection;
  const className = cls ? `${cls.name}${cls.section ? ` – ${cls.section}` : ""}` : "—";
  const addrParts = [school.address, school.city].filter(Boolean);

  return {
    schoolName: school.name || "—",
    schoolAddress: addrParts.join(", ") || "",
    schoolPhone: school.phone || "",
    schoolEmail: school.email || "",
    schoolExtra: school.affiliation_info || "",
    schoolLogo: school.logo_data || null,
    principalName: school.principal_name || "",
    studentName: toTitleCase(student.name),
    fatherName: toTitleCase(student.father_name),
    motherName: toTitleCase(student.mother_name),
    admNo: student.admission_no || "—",
    className,
    rollNo: student.roll_no || "—",
    dob: student.dob ? (() => { const p = String(student.dob).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : student.dob; })() : "—",
    session: exam.session || "—",
    examTitle: exam.name || null,
    remark: "",
    subjects: scholastic.map(s => s.subjectName),
    getObtained: (sub, examIdx) => {
      if (examIdx > 0) return null;
      const s = scholastic.find(s => s.subjectName === sub);
      if (!s || s.isAbsent) return null;
      return s.obtained ?? null;
    },
    getMaxMarks: (sub) => {
      const s = scholastic.find(s => s.subjectName === sub);
      return s?.maxMarks || 100;
    },
    isAbsent: (sub, examIdx) => {
      if (examIdx > 0) return false;
      return !!scholastic.find(s => s.subjectName === sub)?.isAbsent;
    },
    totalObtained: result.totalObtained,
    totalMax: result.totalMax,
    percentage: result.percentage,
    grade: gradeForFn(result.percentage ?? 0).g,
    overallPass: (() => {
      // If school has a custom grade scale, derive pass from the scale's threshold.
      // The last band (minPct: 0) is the fail band; the second-to-last's minPct is the pass floor.
      if (rawScale && rawScale.length >= 2) {
        const lastBand = rawScale[rawScale.length - 1];
        if (lastBand.minPct === 0) {
          const passFloor = rawScale[rawScale.length - 2].minPct;
          return (result.percentage ?? 0) >= passFloor;
        }
      }
      return result.overallPass;
    })(),
    rank: result.rank || null,
    coscho: coSch.length > 0 ? coSch.map(s => s.subjectName) : (cfg.coScholasticActivities || DEFAULT_COSCHO),
    getCoSchoMark: (idx) => { const s = coSch[idx]; return s ? (s.obtained ?? null) : null; },
    getCoSchoGrade: (idx) => {
      const s = coSch[idx];
      if (!s) return "—";
      if (s.isAbsent) return "AB";
      if (s.gradeValue && s.gradeValue !== "AB") return s.gradeValue;
      // Subject entered as marks — derive a letter grade from marks
      if (s.obtained !== null && s.obtained !== undefined) {
        if (s.maxMarks) return gradeForFn((s.obtained / s.maxMarks) * 100).g;
        return String(s.obtained); // no max marks defined — show raw mark
      }
      return "—";
    },
    passMarks: (sub) => { const s = scholastic.find(s => s.subjectName === sub); return s?.passMarks ?? 33; },
    attendance: printData.attendance || null,
    remark: printData.remark || "",
    gradeFor: gradeForFn,
    gradeScale: rawScale || null,
  };
}

/* ─── A4 base styles (inline, so card is self-contained for print) ─── */
const A4 = {
  /* Outermost wrapper: provides the thick coloured border on all 4 sides */
  card: {
    width: "794px",
    minHeight: "1123px",
    background: "#fff",
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "12px",
    fontWeight: 500,
    color: "#111111",
    border: "6px solid",          /* colour applied inline: borderColor: theme */
    boxShadow: "0 6px 32px rgba(0,0,0,0.22)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  /* Inner wrapper: accent border on all 4 sides — nested div, 100% reliable */
  innerWrapper: {
    margin: "6px",
    border: "2.5px solid",        /* colour applied inline: borderColor: accent */
    flex: 1,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  inner: {
    padding: "10px 20px 14px",
    flex: 1,
  },
};

/* ════════════════════════════════════════════════════════
   A4 RESULT CARD  (full-size, print-ready)
   Dispatches to Classic or Modern layout based on layoutStyle
   ════════════════════════════════════════════════════════ */
export function FullResultCard({ tpl, cols, printData }) {
  const cfg = parseCfg(tpl.layout_config);
  if (cfg.layoutStyle === "modern") return <ModernResultCard tpl={tpl} cols={cols} printData={printData} />;
  if (cfg.layoutStyle === "quarterly") return <QuarterlyResultCard tpl={tpl} printData={printData} />;
  return <ClassicResultCard tpl={tpl} cols={cols} printData={printData} />;
}

/* ════════════════════════════════════════════════════════
   QUARTERLY layout — single exam with min marks, co-scholastic,
   attendance, teacher remark, and four signature blocks.
   Blue theme, Calibri/Arial font, compact spacing for A4 fit.
   ════════════════════════════════════════════════════════ */
function QuarterlyResultCard({ tpl, printData }) {
  const cfg    = parseCfg(tpl.layout_config);
  const theme  = cfg.themeColor  || "#1A3C6E";
  const accent = cfg.accentColor || "#F59E0B";
  const ds     = buildDataSrc(cfg, printData);

  /* Sans-serif font stack — different from Classic's Times New Roman */
  const font = '"Calibri", "Segoe UI", Arial, sans-serif';

  /* Compact table header cell */
  const thS = {
    background: theme, color: "#fff",
    border: `1px solid ${darken(theme, 18)}`,
    padding: "3px 4px", textAlign: "center",
    fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.3px",
    fontFamily: font,
  };
  /* Compact table data cell */
  const tdS = {
    border: `1px solid ${theme}55`,
    padding: "3px 4px", textAlign: "center",
    fontSize: "11px", fontWeight: 500,
    fontFamily: font,
  };
  /* Info table label cell */
  const lTd = {
    padding: "3px 6px", fontWeight: 700, fontSize: "10.5px", color: theme,
    border: `1px solid ${theme}55`, whiteSpace: "nowrap", width: "13%",
    background: theme + "10", fontFamily: font,
  };
  /* Info table value cell */
  const vTd = {
    padding: "3px 6px", fontSize: "11px", fontWeight: 500, color: "#000",
    border: `1px solid ${theme}55`, width: "20%", fontFamily: font,
  };
  /* Compact section header */
  const QSectionHeader = ({ label }) => (
    <div style={{ background: theme, color: "#fff", fontWeight: 700, fontSize: "10.5px", letterSpacing: "0.8px", padding: "3px 8px", textTransform: "uppercase", marginBottom: 0, borderLeft: `5px solid ${accent}`, display: "flex", alignItems: "center", gap: 6, fontFamily: font }}>
      <span style={{ color: accent, fontSize: 10 }}>▶</span> {label}
    </div>
  );

  return (
    <div style={{ ...A4.card, borderColor: theme, fontFamily: font, fontSize: "10px", minHeight: "1118px" }}>
      <div style={{ ...A4.innerWrapper, borderColor: accent }}>

        {/* ── HEADER ── */}
        <div style={{ background: theme, color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 16px 5px" }}>
            {/* Logo */}
            <div style={{ width: 60, height: 60, background: "rgba(255,255,255,0.15)", borderRadius: 6, border: "2px solid rgba(255,255,255,0.45)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {ds.schoolLogo
                ? <img src={ds.schoolLogo} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} />
                : <img src="/logo.png" alt="" style={{ width: 52, height: 52, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
              }
            </div>
            {/* School info */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", lineHeight: 1.15, fontFamily: font }}>{ds.schoolName}</div>
              <div style={{ fontSize: "10.5px", marginTop: 3, color: "rgba(255,255,255,0.9)", fontWeight: 500, fontFamily: font }}>{ds.schoolAddress}</div>
              <div style={{ fontSize: "10px", marginTop: 2, color: "rgba(255,255,255,0.9)", fontWeight: 600, fontFamily: font }}>
                {[ds.schoolPhone && `Ph: ${ds.schoolPhone}`, ds.schoolEmail && ds.schoolEmail].filter(Boolean).join("   |   ")}
              </div>
              {ds.schoolExtra && <div style={{ fontSize: "9px", marginTop: 2, color: "rgba(255,255,255,0.82)", fontStyle: "italic", fontFamily: font }}>{ds.schoolExtra}</div>}
            </div>
          </div>
          {/* Exam title strip */}
          <div style={{ background: accent, color: theme, textAlign: "center", fontWeight: 800, fontSize: "11.5px", letterSpacing: "1.5px", textTransform: "uppercase", padding: "4px 18px", borderTop: `2px solid ${darken(theme, 10)}`, fontFamily: font }}>
            {ds.examTitle || tpl.name} &nbsp;|&nbsp; Session: {ds.session}
          </div>
        </div>

        {/* ── BODY (tighter padding to fit A4) ── */}
        <div style={{ padding: "6px 14px 6px", flex: 1 }}>

          {/* Student Info */}
          <table style={{ width: "100%", borderCollapse: "collapse", border: `1.5px solid ${theme}`, marginBottom: 5 }}>
            <tbody>
              <tr>
                <td style={lTd}>Student&apos;s Name</td>
                <td style={{ ...vTd, fontWeight: 700, fontSize: "10.5px" }}>{ds.studentName}</td>
                <td style={lTd}>Father&apos;s Name</td>
                <td style={vTd}>{ds.fatherName}</td>
                <td style={lTd}>Mother&apos;s Name</td>
                <td style={vTd}>{ds.motherName}</td>
              </tr>
              <tr>
                <td style={lTd}>Admission No.</td>
                <td style={vTd}>{ds.admNo}</td>
                <td style={lTd}>Class &amp; Section</td>
                <td style={{ ...vTd, fontWeight: 700 }}>{ds.className}</td>
                <td style={lTd}>Roll No.</td>
                <td style={{ ...vTd, fontWeight: 700 }}>{ds.rollNo}</td>
              </tr>
              <tr>
                <td style={lTd}>Date of Birth</td>
                <td style={vTd}>{ds.dob}</td>
                <td style={lTd}>Academic Year</td>
                <td style={vTd}>{ds.session}</td>
                <td style={lTd}>Stream / Group</td>
                <td style={vTd}>General</td>
              </tr>
            </tbody>
          </table>

          {/* Scholastic Marks */}
          <QSectionHeader label="Scholastic Assessment — Marks Obtained" />
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 5, border: `1.5px solid ${theme}` }}>
            <thead>
              <tr>
                <th style={{ ...thS, width: 26 }}>S.No</th>
                <th style={{ ...thS, textAlign: "left", paddingLeft: 8, width: "30%" }}>Subject</th>
                <th style={thS}>Min<br/>Marks</th>
                <th style={thS}>Max<br/>Marks</th>
                <th style={thS}>Marks<br/>Obtained</th>
                <th style={thS}>Grade</th>
                <th style={{ ...thS, width: "17%" }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {ds.subjects.map((sub, si) => {
                const subMax   = ds.getMaxMarks(sub);
                const subMin   = ds.passMarks(sub);
                const obtained = ds.getObtained(sub, 0);
                const isAbs    = ds.isAbsent(sub, 0);
                const display  = isAbs ? "Ab" : (obtained !== null && obtained !== undefined ? obtained : "—");
                const numObt   = (!isAbs && obtained !== null && obtained !== undefined) ? Number(obtained) : 0;
                const pct      = subMax > 0 ? Math.round((numObt / subMax) * 100) : 0;
                const { g, p, color } = isAbs ? { g: "Ab", p: "Absent", color: "#DC2626" } : ds.gradeFor(pct);
                const passes   = !isAbs && numObt >= subMin;
                const bg       = si % 2 === 0 ? "#fff" : theme + "07";
                return (
                  <tr key={si}>
                    <td style={{ ...tdS, background: bg }}>{si + 1}</td>
                    <td style={{ ...tdS, textAlign: "left", fontWeight: 600, background: theme + "0D", paddingLeft: 8 }}>{sub}</td>
                    <td style={{ ...tdS, background: bg }}>{subMin}</td>
                    <td style={{ ...tdS, background: bg }}>{subMax}</td>
                    <td style={{ ...tdS, fontWeight: 700, background: bg }}>{display}</td>
                    <td style={{ ...tdS, fontWeight: 800, color, background: bg }}>{g}</td>
                    <td style={{ ...tdS, fontSize: 10, color: "#444", textAlign: "left", paddingLeft: 6, background: bg }}>{isAbs ? "Absent" : (passes ? p : "Fail")}</td>
                  </tr>
                );
              })}
              {/* Total row — amber background so it prints clearly */}
              {(() => {
                const totalMin = ds.subjects.reduce((sum, sub) => sum + Number(ds.passMarks(sub) || 0), 0);
                const hasAllMarks = ds.subjects.every(sub => ds.isAbsent(sub, 0) || (ds.getObtained(sub, 0) !== null && ds.getObtained(sub, 0) !== undefined));
                const resultLabel = !hasAllMarks ? "—" : ds.percentage != null ? `${ds.percentage}%` : "—";
                const resultColor = "#111";
                return (
                  <tr style={{ background: "#FEF3C7" }}>
                    <td colSpan={2} style={{ padding: "5px 10px", textAlign: "center", fontSize: "10px", fontWeight: 800, border: `1px solid ${theme}`, letterSpacing: "0.8px", fontFamily: font, color: theme }}>GRAND TOTAL</td>
                    <td style={{ padding: "5px 4px", textAlign: "center", border: `1px solid ${theme}`, fontWeight: 700, fontFamily: font, color: "#111" }}>{totalMin}</td>
                    <td style={{ padding: "5px 4px", textAlign: "center", border: `1px solid ${theme}`, fontWeight: 700, fontFamily: font, color: "#111" }}>{ds.totalMax}</td>
                    <td style={{ padding: "5px 4px", textAlign: "center", fontWeight: 900, fontSize: "12px", border: `1px solid ${theme}`, fontFamily: font, color: theme }}>{ds.totalObtained}</td>
                    <td style={{ padding: "5px 4px", textAlign: "center", fontWeight: 900, border: `1px solid ${theme}`, fontFamily: font }}>
                      <span style={{ color: ds.overallPass ? theme : "#DC2626" }}>{ds.grade}</span>
                      {!ds.overallPass && <span style={{ color: "#DC2626", fontWeight: 800, marginLeft: 4 }}>(Fail)</span>}
                    </td>
                    <td style={{ padding: "5px 4px", textAlign: "center", fontWeight: 800, border: `1px solid ${theme}`, fontFamily: font, color: resultColor }}>{resultLabel}</td>
                  </tr>
                );
              })()}
            </tbody>
          </table>

          {/* Co-Scholastic Grades */}
          {ds.coscho && ds.coscho.length > 0 && (
            <>
              <QSectionHeader label="Co-Scholastic Activities, Attitudes &amp; Values" />
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", marginBottom: 5, border: `1.5px solid ${theme}` }}>
                <colgroup>
                  {ds.coscho.map((_, i) => (
                    <col key={i} style={{ width: `${100 / ds.coscho.length}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {ds.coscho.map((a, i) => (
                      <th key={i} style={{ ...thS, padding: "5px 4px", fontSize: "9px", textAlign: "center", wordBreak: "break-word" }}>{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {ds.coscho.map((a, i) => {
                      const grade  = ds.getCoSchoGrade(i);
                      const gColor = grade === "A+" ? "#1D4ED8" : grade === "A" ? "#2563EB" : grade === "B+" ? "#3B82F6" : "#60A5FA";
                      return (
                        <td key={i} style={{ ...tdS, padding: "4px 4px", background: i % 2 === 0 ? theme + "0A" : "#fff", textAlign: "center" }}>
                          <div style={{ fontSize: "11px", fontWeight: 800, color: gColor, fontFamily: font }}>{grade || "—"}</div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* Attendance Summary */}
          <QSectionHeader label="Attendance Summary" />
          <div style={{ display: "flex", border: `1.5px solid ${theme}`, marginBottom: 5 }}>
            {[
              { label: "Working Days", val: ds.attendance ? ds.attendance.workingDays : "—" },
              { label: "Days Present", val: ds.attendance ? ds.attendance.daysPresent : "—" },
              { label: "Days Absent",  val: ds.attendance ? (ds.attendance.workingDays - ds.attendance.daysPresent) : "—" },
              { label: "Attendance %", val: ds.attendance && ds.attendance.workingDays > 0 ? `${Math.round((ds.attendance.daysPresent / ds.attendance.workingDays) * 100)}%` : "—" },
            ].map((item, i, arr) => (
              <div key={i} style={{ flex: 1, textAlign: "center", padding: "4px 4px", borderRight: i < arr.length - 1 ? `1px solid ${theme}55` : "none", background: i % 2 === 0 ? theme + "07" : "#fff" }}>
                <div style={{ fontSize: "8px", color: theme, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.2px", marginBottom: 1, fontFamily: font }}>{item.label}</div>
                <div style={{ fontSize: "11px", fontWeight: 800, color: "#111", fontFamily: font }}>{item.val}</div>
              </div>
            ))}
          </div>

          {/* Class Teacher's Remark */}
          <QSectionHeader label="Class Teacher's Remark" />
          <div style={{ border: `1px solid ${theme}55`, borderLeft: `4px solid ${theme}`, padding: "4px 10px", margin: "0 0 5px", background: theme + "07", fontSize: "10px", minHeight: 24, fontFamily: font }}>
            <span style={{ color: ds.remark ? "#111" : "#aaa", fontStyle: ds.remark ? "italic" : "normal" }}>
              {ds.remark || "—"}
            </span>
          </div>

          {/* Grade Scale — compact inline version */}
          <GradeScaleLegend theme={theme} scale={ds.gradeScale} />

          {/* Signatures */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 6, paddingTop: 4, borderTop: `1.5px solid ${theme}44` }}>
            {["Class Teacher", "Exam Controller", "Principal", "Parent / Guardian"].map((sig, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center" }}>
                <div style={{ height: 44, borderBottom: `1.5px solid ${theme}`, marginBottom: 4 }} />
                <div style={{ fontSize: "10px", fontWeight: 700, color: theme, fontFamily: font }}>{sig}</div>
                {i === 2 && ds.principalName && <div style={{ fontSize: "9px", color: "#333", marginTop: 2, fontWeight: 600, fontFamily: font }}>{ds.principalName}</div>}
                <div style={{ fontSize: "8.5px", color: "#777", marginTop: 2, fontStyle: "italic", fontFamily: font }}>Signature &amp; Stamp</div>
              </div>
            ))}
          </div>

        </div>{/* /body */}

        {/* Footer */}
        <div style={{ borderTop: `1.5px solid ${theme}`, padding: "4px 16px", textAlign: "center", fontSize: "8.5px", color: "#333", background: theme + "08", letterSpacing: "0.1px", fontFamily: font }}>
          Computer-generated report card &nbsp;|&nbsp; <strong style={{ color: theme }}>Volcanic Result Management System</strong>
        </div>

      </div>{/* /innerWrapper */}
    </div>
  );
}

/* ── CLASSIC layout (original) ──────────────────────── */
function ClassicResultCard({ tpl, cols, printData }) {
  const cfg    = parseCfg(tpl.layout_config);
  const theme  = cfg.themeColor  || "#1E3A5F";
  const accent = cfg.accentColor || "#F5C842";

  const ds       = buildDataSrc(cfg, printData);
  const subjects = ds.subjects;
  const maxPer   = 100;
  const defaultExamLabels = {
    1: ["Examination"],
    2: ["Half Yearly Exam", "Annual Exam"],
    3: ["First Term", "Second Term", "Annual Exam"],
    4: ["Periodic Test 1", "Half Yearly", "Periodic Test 2", "Annual Exam"],
  };
  const exams = (cfg.examLabels || []).slice(0, cols).concat(
    defaultExamLabels[cols].slice((cfg.examLabels || []).length)
  );

  const showCoscho         = !!cfg.hasCoScholastic;
  const showGraph          = !!cfg.showGraph;
  const showRank           = cfg.showRank !== false;
  const showRemark         = !!cfg.showTeacherRemark;
  const showPhoto          = !!cfg.showPhoto;
  const marksAndGrade      = cfg.coScholasticScale === "marks_and_grade";
  const showSubjectRemarks = cfg.showSubjectRemarks !== false;
  const showResult         = cfg.showResult !== false;
  const showExamInCharge   = cfg.showExamInCharge !== false;
  const signatureStamp     = cfg.signatureStamp !== false;
  const compact            = !!cfg.compactPrint;
  const largeFonts         = !!cfg.largeFonts;

  /* Per-card label & value td styles (use theme colour so borders match) */
  const labelTd = {
    padding: "6px 10px", fontWeight: 800, fontSize: largeFonts ? "12px" : "10.5px", color: theme,
    border: `1.5px solid ${theme}`, whiteSpace: "nowrap", width: "12%",
    background: theme + "12",
  };
  const valTd = {
    padding: "6px 10px", fontSize: largeFonts ? "13px" : "11px", fontWeight: 500, color: "#000",
    border: `1.5px solid ${theme}`, width: "22%",
  };

  return (
    /* ── OUTER border: 6px solid theme, all 4 sides ── */
    <div style={{ ...A4.card, borderColor: theme }}>

      {/* ── INNER accent border: nested div, guaranteed all 4 sides ── */}
      <div style={{ ...A4.innerWrapper, borderColor: accent }}>

        {/* ════ SCHOOL HEADER ════ */}
        <div style={{ background: theme, color: "#fff" }}>

          {/* Top band: logo + school name + contact + photo */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px 12px" }}>

            {/* School Logo */}
            <div style={{ width: 92, height: 92, background: "rgba(255,255,255,0.15)", borderRadius: 8, border: "2.5px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {ds.schoolLogo
                ? <img src={ds.schoolLogo} alt="" style={{ width: 82, height: 82, objectFit: "contain" }} />
                : <img src="/logo.png" alt="" style={{ width: 82, height: 82, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
              }
            </div>

            {/* School info — centre */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "26px", fontWeight: 900, letterSpacing: "2.5px", textTransform: "uppercase", lineHeight: 1.1, textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>
                {ds.schoolName}
              </div>
              <div style={{ fontSize: "12px", marginTop: 6, color: "#fff", fontWeight: 500, letterSpacing: "0.3px" }}>
                {ds.schoolAddress}
              </div>
              <div style={{ fontSize: "11.5px", marginTop: 4, color: "#fff", fontWeight: 600, letterSpacing: "0.3px" }}>
                {[ds.schoolPhone && `📞 ${ds.schoolPhone}`, ds.schoolEmail && `✉ ${ds.schoolEmail}`].filter(Boolean).join("     ")}
              </div>
              <div style={{ fontSize: "10.5px", marginTop: 4, color: "rgba(255,255,255,0.88)", fontStyle: "italic" }}>
                {ds.schoolExtra}
              </div>
            </div>

            {/* Photo */}
            {showPhoto && (
              <div style={{ width: 78, height: 92, border: "2.5px dashed rgba(255,255,255,0.6)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "rgba(255,255,255,0.8)", flexShrink: 0, flexDirection: "column", gap: 4 }}>
                {printData?.student?.photo
                  ? <img src={printData.student.photo} alt="" style={{ width: 78, height: 92, objectFit: "cover", borderRadius: 2 }} />
                  : <><span style={{ fontSize: 24 }}>👤</span><span>PHOTO</span></>
                }
              </div>
            )}
          </div>

          {/* Exam title banner — accent strip */}
          <div style={{ background: accent, color: theme, textAlign: "center", fontWeight: 900, fontSize: "14px", letterSpacing: "2px", textTransform: "uppercase", padding: "8px 20px", borderTop: `2px solid ${theme}` }}>
            ✦ &nbsp; {ds.examTitle || tpl.name} &nbsp;—&nbsp; Academic Session: {ds.session} &nbsp; ✦
          </div>
        </div>
        {/* ════ END HEADER ════ */}

        {/* ── Body content ── */}
        <div style={{ ...A4.inner, ...(compact ? { padding: "6px 16px 8px" } : {}) }}>

          {/* STUDENT INFO TABLE */}
          <table style={{ width: "100%", borderCollapse: "collapse", border: `2px solid ${theme}`, marginBottom: 10 }}>
            <tbody>
              <tr>
                <td style={labelTd}>Student's Name</td>
                <td style={{ ...valTd, fontWeight: 800, fontSize: largeFonts ? "14px" : "12px" }}>{ds.studentName}</td>
                <td style={labelTd}>Father's Name</td>
                <td style={valTd}>{ds.fatherName}</td>
                <td style={labelTd}>Mother's Name</td>
                <td style={valTd}>{ds.motherName}</td>
              </tr>
              <tr>
                <td style={labelTd}>Admission No.</td>
                <td style={valTd}>{ds.admNo}</td>
                <td style={labelTd}>Class &amp; Section</td>
                <td style={{ ...valTd, fontWeight: 700 }}>{ds.className}</td>
                <td style={labelTd}>Roll No.</td>
                <td style={{ ...valTd, fontWeight: 700 }}>{ds.rollNo}</td>
              </tr>
              <tr>
                <td style={labelTd}>Date of Birth</td>
                <td style={valTd}>{ds.dob}</td>
                <td style={labelTd}>Academic Year</td>
                <td style={valTd}>{ds.session}</td>
                <td style={labelTd}>Stream / Group</td>
                <td style={valTd}>General</td>
              </tr>
            </tbody>
          </table>

          {/* SCHOLASTIC MARKS */}
          <SectionHeader theme={theme} accent={accent} label="SCHOLASTIC ASSESSMENT — MARKS OBTAINED" />
          <MarksTable cols={cols} exams={exams} subjects={subjects} theme={theme} maxPer={maxPer} ds={ds} showRemarks={showSubjectRemarks} compact={compact} largeFonts={largeFonts} />

          {/* GRAPH */}
          {showGraph && cols > 1 && (
            <>
              <SectionHeader theme={theme} accent={accent} label="PERFORMANCE GRAPH" />
              <GraphSection exams={exams} subjects={subjects} theme={theme} ds={ds} />
            </>
          )}
          {showGraph && cols === 1 && (
            <>
              <SectionHeader theme={theme} accent={accent} label="SUBJECT-WISE PERFORMANCE ANALYSIS" />
              <SingleExamGraph subjects={subjects} theme={theme} accent={accent} maxPer={maxPer} ds={ds} />
            </>
          )}

          {/* CO-SCHOLASTIC */}
          {showCoscho && (
            <>
              <SectionHeader theme={theme} accent={accent} label="CO-SCHOLASTIC ACTIVITIES, ATTITUDES &amp; VALUES" />
              <CoScholasticTable coscho={ds.coscho} theme={theme} marksAndGrade={marksAndGrade} ds={ds} />
            </>
          )}

          {/* TEACHER REMARK */}
          {showRemark && (
            <div style={{ border: `1.5px solid ${theme}`, borderLeft: `5px solid ${theme}`, padding: "6px 12px", margin: "8px 0", background: theme + "0C", fontSize: "11px" }}>
              <strong style={{ color: theme }}>Class Teacher's Remark: </strong>{ds.remark}
            </div>
          )}

          {/* RESULT SUMMARY */}
          <ResultSummary theme={theme} accent={accent} showRank={showRank}
            subjects={subjects} exams={exams} maxPer={maxPer} ds={ds} showResult={showResult} />

          {/* GRADE SCALE */}
          <GradeScaleLegend theme={theme} scale={ds.gradeScale} />

          {/* SIGNATURES */}
          {(() => {
            const sigLabels = ["Class Teacher", ...(showExamInCharge ? ["Exam In-Charge"] : []), "Principal", "Parent / Guardian"];
            return (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: compact ? 8 : 16, gap: 14, paddingTop: 6, borderTop: `2px solid ${theme}22` }}>
                {sigLabels.map((sig, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ height: compact ? 34 : 46, borderBottom: `2px solid ${theme}`, marginBottom: 6 }} />
                    <div style={{ fontSize: "11px", fontWeight: 800, color: theme }}>{sig}</div>
                    {sig === "Principal" && ds.principalName && <div style={{ fontSize: 10, color: "#333", marginTop: 2, fontWeight: 600 }}>{ds.principalName}</div>}
                    <div style={{ fontSize: 9.5, color: "#666", marginTop: 2, fontStyle: "italic" }}>{signatureStamp ? "Signature \u0026 Stamp" : "Signature"}</div>
                  </div>
                ))}
              </div>
            );
          })()}

        </div>{/* /inner */}

        {/* FOOTER */}
        <div style={{ borderTop: `2px solid ${theme}`, padding: "6px 20px", textAlign: "center", fontSize: 10, color: "#222", fontFamily: "Arial, sans-serif", background: theme + "0C", letterSpacing: "0.2px" }}>
          This is a computer-generated report card and does not require a physical signature unless stamped. &nbsp;|&nbsp;
          Generated by <strong style={{ color: theme }}>Volcanic Result Management System</strong>
        </div>

      </div>{/* /innerWrapper */}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MODERN layout — completely different visual structure
   ════════════════════════════════════════════════════════ */
function ModernResultCard({ tpl, cols, printData }) {
  const cfg         = parseCfg(tpl.layout_config);
  const theme       = cfg.themeColor  || "#1565C0";
  const accent      = cfg.accentColor || "#FFC107";

  const ds          = buildDataSrc(cfg, printData);
  const subjects    = ds.subjects;
  const maxPer      = 100;
  const defaultExamLabels = { 1: ["Examination"], 2: ["Half Yearly Exam", "Annual Exam"], 3: ["First Term", "Second Term", "Annual Exam"], 4: ["PT-1", "HY", "PT-2", "Annual"] };
  const exams       = (cfg.examLabels || []).slice(0, cols).concat(defaultExamLabels[cols].slice((cfg.examLabels || []).length));
  const showCoscho  = !!cfg.hasCoScholastic;
  const showGraph   = !!cfg.showGraph;
  const showRank    = cfg.showRank !== false;
  const showRemark         = !!cfg.showTeacherRemark;
  const showExamInCharge   = cfg.showExamInCharge !== false;
  const signatureStamp     = cfg.signatureStamp !== false;
  const compact            = !!cfg.compactPrint;
  const showPhoto   = !!cfg.showPhoto;
  const marksAndGrade = cfg.coScholasticScale === "marks_and_grade";

  const overallPct  = ds.percentage ?? Math.round(ds.totalObtained / (ds.totalMax || 1) * 100);

  // Blue-only grade colour map (no green) — covers CBSE codes (A1/A2…) and simple letter grades (A/B+…)
  const GRADE_COLORS = {
    A1: "#1565C0", A2: "#1976D2",
    B1: "#2196F3", B2: "#42A5F5",
    C1: "#D97706", C2: "#EA580C",
    D:  "#DC2626", E:  "#991B1B",
    "A+": "#1565C0", "A": "#1976D2",
    "B+": "#2196F3", "B": "#42A5F5",
    "C":  "#D97706",
  };
  const gradeColor = (g) => GRADE_COLORS[g] || theme;

  /* Shared th style for marks table */
  const mTh = (extra = {}) => ({
    background: theme, color: "#fff",
    border: `1.5px solid ${darken(theme, 18)}`,
    padding: "7px 6px", textAlign: "center",
    fontSize: "10px", fontWeight: 800,
    ...extra,
  });
  /* Shared td style for marks table */
  const mTd = (extra = {}) => ({
    border: `1.5px solid ${theme}99`,
    padding: "6px 5px", textAlign: "center",
    fontSize: "10.5px", fontWeight: 500, color: "#111",
    ...extra,
  });

  /* Modern section title */
  const ModTitle = ({ label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 0", background: theme, padding: "6px 12px", borderLeft: `6px solid ${accent}` }}>
      <span style={{ color: accent, fontSize: 12, fontWeight: 900 }}>▶</span>
      <span style={{ fontSize: "11px", fontWeight: 900, color: "#fff", letterSpacing: "1px", textTransform: "uppercase" }}>{label}</span>
    </div>
  );

  return (
    /* Outer card: double border via nested wrappers */
    <div style={{ ...A4.card, fontFamily: '"Segoe UI", Arial, sans-serif', borderColor: theme }}>
      <div style={{ ...A4.innerWrapper, borderColor: accent }}>

        {/* ════ HEADER ════ */}
        <div style={{ background: theme, color: "#fff" }}>

          {/* School name + contact band */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px 12px" }}>

            {/* Logo */}
            <div style={{ width: 90, height: 90, background: "rgba(255,255,255,0.15)", borderRadius: 8, border: "2.5px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {ds.schoolLogo
                ? <img src={ds.schoolLogo} alt="" style={{ width: 80, height: 80, objectFit: "contain" }} />
                : <img src="/logo.png" alt="" style={{ width: 80, height: 80, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
              }
            </div>

            {/* School info */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: "26px", fontWeight: 900, letterSpacing: "2px", textTransform: "uppercase", lineHeight: 1.1, textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>
                {ds.schoolName}
              </div>
              <div style={{ fontSize: "12px", marginTop: 6, color: "#fff", fontWeight: 500 }}>
                {ds.schoolAddress}
              </div>
              <div style={{ fontSize: "11.5px", marginTop: 4, color: "#fff", fontWeight: 600 }}>
                {[ds.schoolPhone && `📞 ${ds.schoolPhone}`, ds.schoolEmail && `✉ ${ds.schoolEmail}`].filter(Boolean).join("     ")}
              </div>
              <div style={{ fontSize: "10.5px", marginTop: 4, color: "rgba(255,255,255,0.88)", fontStyle: "italic" }}>
                {ds.schoolExtra}
              </div>
            </div>

            {/* Photo */}
            {showPhoto && (
              <div style={{ width: 78, height: 92, border: "2.5px dashed rgba(255,255,255,0.6)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "rgba(255,255,255,0.8)", flexShrink: 0, flexDirection: "column", gap: 4 }}>
                {printData?.student?.photo
                  ? <img src={printData.student.photo} alt="" style={{ width: 78, height: 92, objectFit: "cover", borderRadius: 2 }} />
                  : <><span style={{ fontSize: 24 }}>👤</span><span>PHOTO</span></>
                }
              </div>
            )}
          </div>

          {/* Exam title strip */}
          <div style={{ background: accent, color: theme, textAlign: "center", fontWeight: 900, fontSize: "14px", letterSpacing: "2px", textTransform: "uppercase", padding: "8px 20px", borderTop: `2px solid ${darken(theme, 10)}` }}>
            ✦ &nbsp; {ds.examTitle || tpl.name} &nbsp;—&nbsp; Academic Session: {ds.session} &nbsp; ✦
          </div>
        </div>
        {/* ════ END HEADER ════ */}

        <div style={{ padding: "10px 20px 12px" }}>

          {/* ── STUDENT INFO TABLE ── */}
          <table style={{ width: "100%", borderCollapse: "collapse", border: `2px solid ${theme}`, marginBottom: 10 }}>
            <tbody>
              {[
                [["Student's Name", ds.studentName, true], ["Father's Name", ds.fatherName, false], ["Mother's Name", ds.motherName, false]],
                [["Admission No.", ds.admNo, false], ["Class & Section", ds.className, true], ["Roll No.", ds.rollNo, true]],
                [["Date of Birth", ds.dob, false], ["Academic Year", ds.session, false], ["Stream", "General", false]],
              ].map((row, ri) => (
                <tr key={ri}>
                  {row.map(([label, val, bold], ci) => (
                    <React.Fragment key={ci}>
                      <td style={{ padding: "6px 10px", fontWeight: 800, fontSize: "10.5px", color: theme, border: `1.5px solid ${theme}`, whiteSpace: "nowrap", background: theme + "12", width: "14%" }}>{label}</td>
                      <td style={{ padding: "6px 10px", fontSize: bold ? "12px" : "11px", fontWeight: bold ? 800 : 500, color: "#000", border: `1.5px solid ${theme}`, width: "19%" }}>{val}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── SCHOLASTIC MARKS ── */}
          <ModTitle label="Scholastic Assessment — Marks Obtained" />
          <table style={{ width: "100%", borderCollapse: "collapse", border: `2px solid ${theme}`, marginBottom: 8 }}>
            <thead>
              <tr>
                <th style={mTh({ width: 30 })}>S.No</th>
                <th style={mTh({ textAlign: "left", paddingLeft: 10, width: cols === 1 ? 220 : 160 })}>Subject</th>
                {cols === 1 ? (
                  <>
                    <th style={mTh()}>Max Marks</th>
                    <th style={mTh()}>Marks Obtained</th>
                    <th style={mTh()}>Percentage</th>
                    <th style={mTh()}>Grade</th>
                    <th style={mTh({ width: 110 })}>Performance Bar</th>
                  </>
                ) : (
                  <>
                    {exams.map((e, i) => (
                      <th key={i} colSpan={cols === 4 ? 2 : 3} style={mTh({ background: darken(theme, i * 10) })}>
                        {e}{cols < 4 && <span style={{ display: "block", fontWeight: 400, fontSize: "8.5px" }}>Max: {maxPer}</span>}
                      </th>
                    ))}
                    <th style={mTh({ color: accent })}>Grand Total<span style={{ display: "block", fontWeight: 400, fontSize: "8.5px", color: "#fff" }}>Max: {cols * maxPer}</span></th>
                    <th style={mTh()}>%</th>
                    <th style={mTh()}>Grade</th>
                    {cols < 4 && <th style={mTh()}>Result</th>}
                  </>
                )}
              </tr>
              {cols > 1 && (
                <tr>
                  <th style={mTh({ background: darken(theme, 15), fontSize: "9px" })} colSpan={2}></th>
                  {exams.map((_, i) => (
                    cols === 4
                      ? <React.Fragment key={i}><th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}>Obt.</th><th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}>Grd</th></React.Fragment>
                      : <React.Fragment key={i}><th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}>MM</th><th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}>MO</th><th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}>Grd</th></React.Fragment>
                  ))}
                  <th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}></th>
                  <th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}></th>
                  <th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}></th>
                  {cols < 4 && <th style={mTh({ background: darken(theme, 15), fontSize: "9px" })}></th>}
                </tr>
              )}
            </thead>
            <tbody>
              {subjects.map((sub, si) => {
                const subMax = ds.getMaxMarks(sub);
                const obtainedArr = exams.map((_, i) => {
                  const o = ds.getObtained(sub, i);
                  return o !== null && o !== undefined ? o : null;
                });
                const firstObtained = ds.getObtained(sub, 0);
                const isAbs = ds.isAbsent(sub, 0);
                const displayObtained = isAbs ? "Ab" : (firstObtained !== null && firstObtained !== undefined ? firstObtained : (obtainedArr[0] !== null && obtainedArr[0] !== undefined ? obtainedArr[0] : "—"));
                const total = exams.reduce((s, _, i) => s + (obtainedArr[i] ?? 0), 0);
                const pct   = Math.round(total / (exams.length * subMax) * 100);
                const passMarksPct = subMax > 0 ? Math.round((ds.passMarks(sub) / subMax) * 100) : 33;
                const { g }   = ds.gradeFor(pct);
                const singlePct = isAbs ? 0 : Math.round(((typeof displayObtained === "number" ? displayObtained : 0) / subMax) * 100);
                const { g: sg } = ds.gradeFor(singlePct);
                const bg = si % 2 === 0 ? "#fff" : theme + "08";
                return (
                  <tr key={si} style={{ background: bg }}>
                    <td style={mTd({ background: bg })}>{si + 1}</td>
                    <td style={mTd({ textAlign: "left", fontWeight: 700, background: theme + "10", paddingLeft: 10 })}>{sub}</td>
                    {cols === 1 ? (
                      <>
                        <td style={mTd({ background: bg })}>{subMax}</td>
                        <td style={mTd({ fontWeight: 800, fontSize: "11px", background: bg })}>{isAbs ? "Ab" : displayObtained}</td>
                        <td style={mTd({ fontWeight: 700, background: bg })}>{isAbs ? "—" : `${singlePct}%`}</td>
                        <td style={mTd({ background: bg })}>
                          <span style={{ background: gradeColor(sg) + "22", color: gradeColor(sg), border: `1.5px solid ${gradeColor(sg)}88`, borderRadius: 8, padding: "2px 8px", fontSize: 10, fontWeight: 900, display: "inline-block" }}>{isAbs ? "Ab" : sg}</span>
                        </td>
                        <td style={mTd({ background: bg, padding: "6px 8px" })}>
                          <div style={{ background: "#ddd", borderRadius: 4, height: 10, overflow: "hidden" }}>
                            <div style={{ width: `${isAbs ? 0 : singlePct}%`, height: "100%", background: gradeColor(sg), borderRadius: 4 }} />
                          </div>
                          <div style={{ fontSize: 8.5, fontWeight: 600, color: "#333", marginTop: 2, textAlign: "center" }}>{isAbs ? "Absent" : `${singlePct}%`}</div>
                        </td>
                      </>
                    ) : (
                      <>
                        {exams.map((_, i) => {
                          const ev  = obtainedArr[i];
                          const ep  = ev !== null ? Math.round(ev / subMax * 100) : 0;
                          const { g: eg } = ev !== null ? ds.gradeFor(ep) : { g: "—" };
                          return cols === 4
                            ? <React.Fragment key={i}>
                                <td style={mTd({ background: bg, fontWeight: 700 })}>{ev !== null ? ev : "—"}</td>
                                <td style={mTd({ background: bg, fontWeight: 800, color: gradeColor(eg) })}>{eg}</td>
                              </React.Fragment>
                            : <React.Fragment key={i}>
                                <td style={mTd({ background: bg })}>{subMax}</td>
                                <td style={mTd({ background: bg, fontWeight: 700 })}>{ev !== null ? ev : "—"}</td>
                                <td style={mTd({ background: bg, fontWeight: 800, color: gradeColor(eg) })}>{eg}</td>
                              </React.Fragment>;
                        })}
                        <td style={mTd({ background: bg, fontWeight: 900, fontSize: "11px" })}>{total}</td>
                        <td style={mTd({ background: bg, fontWeight: 700 })}>{pct}%</td>
                        <td style={mTd({ background: bg })}>
                          <span style={{ background: gradeColor(g) + "22", color: gradeColor(g), border: `1.5px solid ${gradeColor(g)}88`, borderRadius: 8, padding: "2px 8px", fontSize: 10, fontWeight: 900, display: "inline-block" }}>{g}</span>
                        </td>
                        {cols < 4 && <td style={mTd({ background: bg, fontWeight: 800, color: pct >= passMarksPct ? theme : "#DC2626" })}>{pct >= passMarksPct ? "PASS" : "FAIL"}</td>}
                      </>
                    )}
                  </tr>
                );
              })}
              {/* Grand total row */}
              <tr style={{ background: theme, color: "#fff", fontWeight: 800 }}>
                <td colSpan={2} style={{ padding: "8px 12px", textAlign: "right", fontSize: "11px", fontWeight: 900, letterSpacing: "1px", border: `1.5px solid ${darken(theme, 15)}` }}>GRAND TOTAL</td>
                {cols === 1 ? (
                  <>
                    <td style={{ padding: "8px 5px", textAlign: "center", border: `1.5px solid ${darken(theme, 15)}` }}>{ds.totalMax}</td>
                    <td style={{ padding: "8px 5px", textAlign: "center", fontWeight: 900, color: accent, fontSize: "12px", border: `1.5px solid ${darken(theme, 15)}` }}>{ds.totalObtained}</td>
                    <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 800, border: `1.5px solid ${darken(theme, 15)}` }}>{ds.percentage}%</td>
                    <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 900, border: `1.5px solid ${darken(theme, 15)}` }}>{ds.grade}</td>
                    <td style={{ border: `1.5px solid ${darken(theme, 15)}` }} />
                  </>
                ) : (
                  <>
                    {exams.map((_, i) => {
                      const examTotal = subjects.reduce((s, sub) => {
                        const o = ds.getObtained(sub, i);
                        return s + (o !== null && o !== undefined ? o : 0);
                      }, 0);
                      return cols === 4
                        ? <React.Fragment key={i}>
                            <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 800, border: `1.5px solid ${darken(theme, 15)}` }}>{examTotal}</td>
                            <td style={{ padding: "8px 5px", textAlign: "center", border: `1.5px solid ${darken(theme, 15)}` }}>—</td>
                          </React.Fragment>
                        : <React.Fragment key={i}>
                            <td style={{ padding: "8px 5px", textAlign: "center", border: `1.5px solid ${darken(theme, 15)}` }}>{subjects.length * maxPer}</td>
                            <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 800, border: `1.5px solid ${darken(theme, 15)}` }}>{examTotal}</td>
                            <td style={{ padding: "8px 5px", textAlign: "center", border: `1.5px solid ${darken(theme, 15)}` }}>—</td>
                          </React.Fragment>;
                    })}
                    <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 900, fontSize: "12px", border: `1.5px solid ${darken(theme, 15)}` }}>{ds.totalObtained}</td>
                    <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 800, border: `1.5px solid ${darken(theme, 15)}` }}>{ds.percentage}%</td>
                    <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 900, border: `1.5px solid ${darken(theme, 15)}` }}>{ds.grade}</td>
                    {cols < 4 && <td style={{ padding: "8px 5px", textAlign: "center", color: accent, fontWeight: 900, border: `1.5px solid ${darken(theme, 15)}` }}>{ds.overallPass ? "PASS" : "FAIL"}</td>}
                  </>
                )}
              </tr>
            </tbody>
          </table>

          {/* ── GRAPH ── */}
          {showGraph && cols > 1 && (
            <>
              <ModTitle label="Performance Graph" />
              <GraphSection exams={exams} subjects={subjects} theme={theme} ds={ds} />
            </>
          )}
          {showGraph && cols === 1 && (
            <>
              <ModTitle label="Subject-wise Performance Analysis" />
              <SingleExamGraph subjects={subjects} theme={theme} accent={accent} maxPer={maxPer} ds={ds} />
            </>
          )}

          {/* ── CO-SCHOLASTIC ── (shown only if enabled) */}
          {showCoscho && (
            <>
              <ModTitle label="Co-Scholastic Activities, Attitudes &amp; Values" />
              <table style={{ width: "100%", borderCollapse: "collapse", border: `2px solid ${theme}`, marginBottom: 8, marginTop: 0 }}>
                <thead>
                  <tr>
                    {[...ds.coscho, "Attendance"].map((a, i) => (
                      <th key={i} style={mTh({ padding: "6px 8px", fontSize: "9.5px" })}>{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {[...ds.coscho, "Attendance"].map((a, i) => {
                      const isAttend = a === "Attendance";
                      const grade = isAttend
                        ? (ds.attendance ? `${ds.attendance.daysPresent} / ${ds.attendance.workingDays}` : "—")
                        : ds.getCoSchoGrade(i);
                      const gColor = isAttend ? theme : gradeColor(grade);
                      return (
                        <td key={i} style={mTd({ padding: "10px 6px", background: i % 2 === 0 ? theme + "0C" : "#fff" })}>
                          <div style={{ fontSize: "15px", fontWeight: 900, color: gColor, textAlign: "center" }}>{grade}</div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* ── ATTENDANCE SUMMARY ── */}
          {!showCoscho && (
            <>
              <ModTitle label="Attendance Summary" />
              <div style={{ display: "flex", border: `2px solid ${theme}`, marginBottom: 8 }}>
                {[
                  { label: "Working Days", val: ds.attendance ? ds.attendance.workingDays : "—" },
                  { label: "Days Present", val: ds.attendance ? ds.attendance.daysPresent : "—" },
                  { label: "Days Absent",  val: ds.attendance ? (ds.attendance.workingDays - ds.attendance.daysPresent) : "—" },
                  { label: "Attendance %", val: ds.attendance && ds.attendance.workingDays > 0 ? `${Math.round((ds.attendance.daysPresent / ds.attendance.workingDays) * 100)}%` : "—" },
                ].map((item, i, arr) => (
                  <div key={i} style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRight: i < arr.length - 1 ? `1px solid ${theme}55` : "none", background: i % 2 === 0 ? theme + "08" : "#fff" }}>
                    <div style={{ fontSize: "9px", color: theme, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: "15px", fontWeight: 900, color: "#111" }}>{item.val}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── TEACHER REMARK ── */}
          {showRemark && (
            <div style={{ border: `1.5px solid ${theme}`, borderLeft: `5px solid ${theme}`, padding: "7px 12px", margin: "8px 0", background: theme + "0C", fontSize: "11px" }}>
              <strong style={{ color: theme, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.5px" }}>Class Teacher's Remark: </strong>
              <span style={{ color: "#111", fontStyle: "italic" }}>{ds.remark}</span>
            </div>
          )}

          {/* ── RESULT SUMMARY ── */}
          <div style={{ display: "grid", gridTemplateColumns: showRank ? "repeat(5, 1fr)" : "repeat(4, 1fr)", gap: 8, margin: "10px 0 8px" }}>
            {[
              { label: "Total Marks",   val: `${ds.totalObtained}`, sub: `out of ${ds.totalMax}`,        color: theme },
              { label: "Percentage",    val: `${ds.percentage}%`,    sub: "overall",                      color: darken(theme, 10) },
              ...(showRank ? [{ label: "Class Rank", val: ds.rank ? `${ds.rank}` : "—", sub: "in class", color: darken(theme, 20) }] : []),
              { label: "Overall Grade", val: ds.grade,               sub: ds.gradeFor(overallPct).p,      color: gradeColor(ds.grade) },
              { label: "Result",        val: ds.overallPass ? "PASS" : "FAIL", sub: ds.overallPass ? "Congratulations!" : "Work Harder", color: ds.overallPass ? theme : "#DC2626" },
            ].map((item, i) => (
              <div key={i} style={{ background: "#fff", border: `1.5px solid ${item.color}44`, borderTop: `4px solid ${item.color}`, borderRadius: 6, padding: "10px 6px", textAlign: "center" }}>
                <div style={{ fontSize: "9.5px", color: "#333", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5 }}>{item.label}</div>
                <div style={{ fontSize: item.label === "Result" ? 17 : 15, fontWeight: 900, color: item.color }}>{item.val}</div>
                <div style={{ fontSize: "9px", color: "#444", marginTop: 3, fontWeight: 500 }}>{item.sub}</div>
              </div>
            ))}
          </div>

          {/* ── GRADE SCALE ── */}
          <GradeScaleLegend theme={theme} scale={ds.gradeScale} />

          {/* ── SIGNATURES ── */}
          {(() => {
            const sigLabels = ["Class Teacher", ...(showExamInCharge ? ["Exam In-Charge"] : []), "Principal", "Parent / Guardian"];
            return (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: compact ? 8 : 14, gap: 12, paddingTop: 6, borderTop: `2px solid ${theme}22` }}>
                {sigLabels.map((sig, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ height: compact ? 34 : 44, borderBottom: `2px solid ${theme}`, marginBottom: 6 }} />
                    <div style={{ fontSize: "11px", fontWeight: 800, color: theme }}>{sig}</div>
                    {sig === "Principal" && ds.principalName && <div style={{ fontSize: "10px", color: "#222", marginTop: 2, fontWeight: 600 }}>{ds.principalName}</div>}
                    <div style={{ fontSize: "9.5px", color: "#555", marginTop: 2, fontStyle: "italic" }}>{signatureStamp ? "Signature \u0026 Stamp" : "Signature"}</div>
                  </div>
                ))}
              </div>
            );
          })()}

        </div>{/* /body */}

        {/* ── FOOTER ── */}
        <div style={{ borderTop: `2px solid ${theme}`, padding: "6px 20px", textAlign: "center", fontSize: "10px", color: "#222", fontFamily: "Arial, sans-serif", background: theme + "0C", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <div style={{ width: 20, height: 4, background: `linear-gradient(90deg, ${theme}, ${accent})`, borderRadius: 2 }} />
          Computer-generated report card &nbsp;|&nbsp; <strong style={{ color: theme }}>Volcanic Result Management System</strong> &nbsp;|&nbsp; Volcanic Education &amp; Technology Pvt Ltd
          <div style={{ width: 20, height: 4, background: `linear-gradient(90deg, ${accent}, ${theme})`, borderRadius: 2 }} />
        </div>

      </div>{/* /innerWrapper */}
    </div>
  );
}

/* ── Section header bar ─────────────────────────────── */
function SectionHeader({ theme, accent, label }) {
  return (
    <div style={{ background: theme, color: "#fff", fontWeight: 900, fontSize: "11px", letterSpacing: "1px", padding: "6px 12px", textTransform: "uppercase", marginBottom: 0, borderLeft: `6px solid ${accent}`, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ color: accent, fontSize: 12 }}>▶</span> {label}
    </div>
  );
}

/* (info td styles are now defined inline in ClassicResultCard to use theme colour) */

/* ── Marks Table (adapts to 1/2/3/4 exams) ─────────── */
function MarksTable({ cols, exams, subjects, theme, maxPer, ds, showRemarks = true, compact = false, largeFonts = false }) {
  const fs = largeFonts
    ? { th: "12px", thSub: "11px", td: "12.5px", table: cols === 4 ? "11.5px" : "12.5px" }
    : { th: "10px", thSub: "9.5px", td: "10.5px", table: cols === 4 ? "9.5px" : "10.5px" };
  const thStyle = { background: theme, color: "#fff", border: `1.5px solid ${darken(theme, 20)}`, padding: compact ? "5px 4px" : "7px 5px", textAlign: "center", fontSize: fs.th, fontWeight: 800, letterSpacing: "0.4px" };
  const thSub   = { ...thStyle, background: darken(theme, 18), fontSize: fs.thSub, fontWeight: 700, padding: compact ? "4px 3px" : "5px 4px" };
  const tdStyle = { border: `1.5px solid ${theme}`, padding: compact ? "4px 4px" : "6px 5px", textAlign: "center", fontSize: fs.td, fontWeight: 500 };
  const tdSubj  = { ...tdStyle, textAlign: "left", fontWeight: 700, background: theme + "10", paddingLeft: 10 };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, border: `2px solid ${theme}`, fontSize: fs.table }}>
      <thead>
        <tr>
          <th style={{ ...thStyle, width: 22 }}>S.No</th>
          <th style={{ ...thStyle, textAlign: "left", paddingLeft: 8, width: cols === 4 ? 130 : cols === 3 ? 150 : 200 }}>Subject</th>
          {cols === 1 ? (
            <>
              <th style={thStyle}>Max Marks</th>
              <th style={thStyle}>Marks Obtained</th>
              <th style={thStyle}>%</th>
              <th style={thStyle}>Grade</th>
              {showRemarks && <th style={{ ...thStyle, width: 90 }}>Remarks</th>}
            </>
          ) : (
            <>
              {exams.map((e, i) => (
                <th key={i} colSpan={cols === 4 ? 2 : 3} style={thStyle}>
                  {e}{cols < 4 && <span style={{ display: "block", fontWeight: 400, fontSize: 8 }}>Max: {maxPer}</span>}
                </th>
              ))}
              <th style={thStyle}>Grand<br/>Total<br/><span style={{ fontWeight: 400, fontSize: 8 }}>Max: {cols * maxPer}</span></th>
              <th style={thStyle}>%</th>
              <th style={thStyle}>Grade</th>
              {cols < 4 && <th style={thStyle}>Result</th>}
            </>
          )}
        </tr>
        {cols > 1 && (
          <tr>
            <th style={thSub} colSpan={2}></th>
            {exams.map((_, i) => (
              cols === 4
                ? <React.Fragment key={i}><th style={thSub}>Obt.</th><th style={thSub}>Grd</th></React.Fragment>
                : <React.Fragment key={i}><th style={thSub}>MM</th><th style={thSub}>MO</th><th style={thSub}>Grd</th></React.Fragment>
            ))}
            <th style={thSub}></th><th style={thSub}></th><th style={thSub}></th>
            {cols < 4 && <th style={thSub}></th>}
          </tr>
        )}
      </thead>
      <tbody>
        {subjects.map((sub, si) => {
          const subMax = ds.getMaxMarks(sub);
          const obtainedArr = exams.map((_, i) => {
            const o = ds.getObtained(sub, i);
            return o !== null && o !== undefined ? o : null;
          });
          const isAbs = ds.isAbsent(sub, 0);
          const singleObtained = ds.getObtained(sub, 0);
          const displaySingle = isAbs ? "Ab" : (singleObtained !== null && singleObtained !== undefined ? singleObtained : (obtainedArr[0] !== null && obtainedArr[0] !== undefined ? obtainedArr[0] : "—"));
          const total = exams.reduce((s, _, i) => s + (obtainedArr[i] || 0), 0);
          const pct   = Math.round(total / (exams.length * subMax) * 100);
          const passMarksPct = subMax > 0 ? Math.round((ds.passMarks(sub) / subMax) * 100) : 33;
          const { g, p, color } = ds.gradeFor(pct);
          const singlePct = isAbs ? 0 : Math.round(((displaySingle !== "Ab" ? displaySingle : 0) / subMax) * 100);
          const bg    = si % 2 === 0 ? "#fff" : theme + "08";
          return (
            <tr key={si} style={{ background: bg }}>
              <td style={{ ...tdStyle, background: bg }}>{si + 1}</td>
              <td style={{ ...tdSubj, background: bg }}>{sub}</td>
              {cols === 1 ? (
                <>
                  <td style={tdStyle}>{subMax}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{isAbs ? "Ab" : displaySingle}</td>
                  <td style={tdStyle}>{isAbs ? "—" : `${singlePct}%`}</td>
                  <td style={{ ...tdStyle, fontWeight: 800, color: isAbs ? "#DC2626" : ds.gradeFor(singlePct).color }}>{isAbs ? "Ab" : ds.gradeFor(singlePct).g}</td>
                  {showRemarks && <td style={{ ...tdStyle, textAlign: "left", fontSize: 9.5, color: "#444" }}>{isAbs ? "Absent" : ds.gradeFor(singlePct).p}</td>}
                </>
              ) : (
                <>
                  {exams.map((_, i) => {
                    const ev = obtainedArr[i];
                    const ep = ev !== null ? Math.round(ev / subMax * 100) : 0;
                    const eg = ev !== null ? ds.gradeFor(ep) : { g: "—", color: "#94A3B8" };
                    return cols === 4
                      ? <React.Fragment key={i}>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{ev !== null ? ev : "—"}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: eg.color }}>{eg.g}</td>
                        </React.Fragment>
                      : <React.Fragment key={i}>
                          <td style={tdStyle}>{subMax}</td>
                          <td style={{ ...tdStyle, fontWeight: 700 }}>{ev !== null ? ev : "—"}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: eg.color }}>{eg.g}</td>
                        </React.Fragment>;
                  })}
                  <td style={{ ...tdStyle, fontWeight: 800 }}>{total}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{pct}%</td>
                  <td style={{ ...tdStyle, fontWeight: 800, color }}>{g}</td>
                  {cols < 4 && <td style={{ ...tdStyle, color: pct >= passMarksPct ? "#15803D" : "#DC2626", fontWeight: 700 }}>{pct >= passMarksPct ? "PASS" : "FAIL"}</td>}
                </>
              )}
            </tr>
          );
        })}
        {/* Total row */}
        <tr style={{ background: theme + "22", fontWeight: 700 }}>
          <td colSpan={2} style={{ ...tdStyle, textAlign: "right", fontWeight: 900, background: theme + "22", paddingRight: 10, color: theme }}>GRAND TOTAL</td>
          {cols === 1 ? (
            <>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 700 }}>{ds.totalMax}</td>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 800 }}>{ds.totalObtained}</td>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 700 }}>{ds.percentage}%</td>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 900, color: ds.gradeFor(ds.percentage ?? 0).color }}>{ds.grade}</td>
              {showRemarks && <td style={{ ...tdStyle, background: theme + "22", fontWeight: 700, color: ds.gradeFor(ds.percentage ?? 0).color }}>{ds.gradeFor(ds.percentage ?? 0).p}</td>}
            </>
          ) : (
            <>
              {exams.map((_, i) => {
                const examTotal = subjects.reduce((s, sub) => {
                  const o = ds.getObtained(sub, i);
                  return s + (o !== null && o !== undefined ? o : 0);
                }, 0);
                return cols === 4
                  ? <React.Fragment key={i}>
                      <td style={{ ...tdStyle, background: theme + "22", fontWeight: 700 }}>{examTotal}</td>
                      <td style={{ ...tdStyle, background: theme + "22" }}>—</td>
                    </React.Fragment>
                  : <React.Fragment key={i}>
                      <td style={{ ...tdStyle, background: theme + "22", fontWeight: 600 }}>{subjects.length * maxPer}</td>
                      <td style={{ ...tdStyle, background: theme + "22", fontWeight: 800 }}>{examTotal}</td>
                      <td style={{ ...tdStyle, background: theme + "22" }}>—</td>
                    </React.Fragment>;
              })}
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 800 }}>{ds.totalObtained}</td>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 700 }}>{ds.percentage}%</td>
              <td style={{ ...tdStyle, background: theme + "22", fontWeight: 900, color: ds.gradeFor(ds.percentage ?? 0).color }}>{ds.grade}</td>
              {cols < 4 && <td style={{ ...tdStyle, background: theme + "22", fontWeight: 800, color: ds.overallPass ? "#15803D" : "#DC2626" }}>{ds.overallPass ? "PASS" : "FAIL"}</td>}
            </>
          )}
        </tr>
      </tbody>
    </table>
  );
}

/* ── Performance Bar Graph ──────────────────────────── */
function GraphSection({ exams, subjects, theme, ds }) {
  return (
    <div style={{ border: `1px solid ${theme}44`, padding: "10px 12px", marginBottom: 8, background: "#FAFBFC" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height: 110, paddingBottom: 4 }}>
        {subjects.map((sub, si) => {
          const m = exams.map((_, i) => {
            const o = ds.getObtained(sub, i);
            return o !== null && o !== undefined ? o : 0;
          });
          return (
            <div key={si} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 90 }}>
                {exams.map((_, i) => (
                  <div key={i} title={`${exams[i]}: ${m[i]}`} style={{
                    width: Math.max(8, Math.floor(40 / exams.length)),
                    height: `${m[i]}%`, maxHeight: 90,
                    background: BAR_COLORS[i % BAR_COLORS.length],
                    borderRadius: "2px 2px 0 0",
                    display: "flex", alignItems: "flex-start", justifyContent: "center",
                  }}>
                    <span style={{ fontSize: 7, color: "#fff", marginTop: 2, fontWeight: 700 }}>{m[i]}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 9, textAlign: "center", color: "#222", fontWeight: 600, lineHeight: 1.2 }}>{sub.split(" ")[0]}</div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, paddingTop: 6, borderTop: "1px solid #ccc" }}>
        {exams.map((e, i) => (
          <span key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#222", fontWeight: 600 }}>
            <span style={{ width: 14, height: 14, background: BAR_COLORS[i % 4], borderRadius: 2, display: "inline-block" }} />
            {e}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Single-Exam Subject-wise Horizontal Bar Graph ──── */
function SingleExamGraph({ subjects, theme, accent, maxPer, ds }) {
  const GRADE_COLORS = {
    A1: "#1565C0", A2: "#1976D2",
    B1: "#2196F3", B2: "#42A5F5",
    C1: "#D97706", C2: "#EA580C",
    D:  "#DC2626", E:  "#991B1B",
  };
  const defaultPassThresholdPct = subjects.length > 0
    ? Math.round((ds.passMarks(subjects[0]) / (ds.getMaxMarks(subjects[0]) || 100)) * 100)
    : 33;

  return (
    <div style={{ border: "1px solid #E2E8F0", background: "#FAFBFC", padding: "10px 14px", marginBottom: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 36px 36px 52px", gap: "0 8px", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#222", textTransform: "uppercase" }}>Subject</div>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#222", textTransform: "uppercase" }}>Performance</div>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#222", textAlign: "center" }}>Marks</div>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#222", textAlign: "center" }}>%</div>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: "#222", textAlign: "center" }}>Grade</div>
      </div>
      {subjects.map((sub, si) => {
        const subMax   = ds.getMaxMarks(sub);
        const obtained = ds.getObtained(sub, 0) ?? 75;
        const pct = Math.round((obtained / subMax) * 100);
        const subPassThresholdPct = subMax > 0 ? Math.round((ds.passMarks(sub) / subMax) * 100) : defaultPassThresholdPct;
        const { g, color } = ds.gradeFor(pct);
        const barColor = GRADE_COLORS[g] || color || theme;
        const bg = si % 2 === 0 ? "#fff" : "#F7F9FC";
        return (
          <div key={si} style={{ display: "grid", gridTemplateColumns: "110px 1fr 36px 36px 52px", gap: "0 8px", alignItems: "center", padding: "4px 0", background: bg, borderBottom: "1px solid #F1F5F9" }}>
            {/* Subject name */}
            <div style={{ fontSize: 10, fontWeight: 700, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
            {/* Bar track */}
            <div style={{ position: "relative", height: 14, background: "#E2E8F0", borderRadius: 7, overflow: "hidden" }}>
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${barColor}CC, ${barColor})`,
                borderRadius: 7,
                transition: "width 0.3s ease",
              }} />
              {/* Pass threshold line */}
              <div style={{ position: "absolute", left: `${subPassThresholdPct}%`, top: 0, bottom: 0, width: 1, background: "rgba(0,0,0,0.15)" }} />
            </div>
            {/* Marks */}
            <div style={{ fontSize: 10, fontWeight: 800, textAlign: "center", color: "#111" }}>{obtained}<span style={{ fontWeight: 500, fontSize: 9, color: "#555" }}>/{subMax}</span></div>
            {/* % */}
            <div style={{ fontSize: 10, fontWeight: 700, textAlign: "center", color: "#222" }}>{pct}%</div>
            {/* Grade badge */}
            <div style={{ textAlign: "center" }}>
              <span style={{ display: "inline-block", padding: "1px 7px", borderRadius: 10, fontSize: 9, fontWeight: 800, background: barColor + "22", color: barColor, border: `1px solid ${barColor}55` }}>{g}</span>
            </div>
          </div>
        );
      })}
      {/* Footer legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 7, paddingTop: 6, borderTop: "1px solid #ccc", flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, color: "#444", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 1, height: 12, background: "rgba(0,0,0,0.3)", display: "inline-block" }} /> Pass threshold ({defaultPassThresholdPct}%)
        </span>
        {[["A1/A2", "#1565C0"], ["B1/B2", "#2196F3"], ["C1/C2", "#D97706"], ["D/E", "#DC2626"]].map(([label, color]) => (
          <span key={label} style={{ fontSize: 9.5, color: "#333", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 20, height: 8, background: color, borderRadius: 3, display: "inline-block" }} /> {label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Co-Scholastic Table ────────────────────────────── */
function CoScholasticTable({ coscho, theme, marksAndGrade, ds }) {
  const all = [...coscho, "Attendance"];
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8, border: `2px solid ${theme}` }}>
      <thead>
        <tr>
          {all.map((a, i) => (
            <th key={i} style={{ background: theme, color: "#fff", border: `1.5px solid ${darken(theme, 20)}`, padding: "7px 6px", textAlign: "center", fontSize: "10px", fontWeight: 800, width: `${100 / all.length}%` }}>
              {a}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {all.map((a, i) => {
            const isAttend = a === "Attendance";
            const grade = isAttend
              ? (ds.attendance ? `${ds.attendance.daysPresent} / ${ds.attendance.workingDays}` : "—")
              : ds.getCoSchoGrade(i);
            const mark  = isAttend ? null : ds.getCoSchoMark(i);
            const displayMark = marksAndGrade && !isAttend && mark !== null
              ? `${mark}`
              : null;
            return (
              <td key={i} style={{ border: `1.5px solid ${theme}`, padding: "10px 6px", textAlign: "center", background: i % 2 === 0 ? theme + "0C" : "#fff" }}>
                {displayMark && <div style={{ fontSize: 10, fontWeight: 700, color: "#333" }}>{displayMark}</div>}
                <div style={{ fontSize: 14, fontWeight: 800, color: isAttend ? "#555" : "#15803D" }}>{grade}</div>
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

/* ── Result Summary Strip ───────────────────────────── */
function ResultSummary({ theme, accent, showRank, subjects, exams, maxPer, ds, showResult = true }) {
  const pct  = ds.percentage ?? 0;
  const { g, p } = ds.gradeFor(pct);
  const rankVal = ds.rank ? `${ds.rank}` : "—";
  const items = [
    { label: "Total Marks",   val: `${ds.totalObtained} / ${ds.totalMax}` },
    { label: "Percentage",    val: `${pct}%` },
    ...(showRank  ? [{ label: "Class Rank",    val: rankVal }] : []),
    { label: "Overall Grade", val: `${ds.grade} — ${ds.gradeFor(pct).p}` },
    ...(showResult ? [{ label: "Result", val: ds.overallPass ? "PASS" : "FAIL", big: true, pass: ds.overallPass }] : []),
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${items.length}, 1fr)`, background: theme, color: "#fff", borderRadius: 4, margin: "8px 0", overflow: "hidden" }}>
      {items.map((item, i, arr) => (
        <div key={i} style={{ padding: "10px 6px", textAlign: "center", borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.25)" : "none" }}>
          <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.88)", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>{item.label}</div>
          <div style={{ fontSize: item.big ? 18 : 14, fontWeight: 900, color: item.pass === false ? "#FCA5A5" : item.pass ? "#4ADE80" : accent }}>
            {item.val}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Grade scale legend ─────────────────────────────── */
function GradeScaleLegend({ theme, scale }) {
  // Build display rows from custom scale or fall back to hardcoded CBSE default
  const bands = (scale && scale.length > 0)
    ? scale.map((band, i) => {
        const nextBand = scale[i + 1];
        const rangeLabel = nextBand
          ? `${nextBand.minPct}–${band.minPct > 0 ? band.minPct + (i === 0 ? "+" : "") : band.minPct}`
          : `<${band.minPct === 0 ? scale[i - 1]?.minPct ?? 33 : band.minPct}`;
        // Simpler range: show minPct+
        const rangeStr = i === 0
          ? `${band.minPct}+`
          : nextBand
            ? `${band.minPct}–${scale[i - 1].minPct - 1}`
            : `<${band.minPct === 0 ? (scale[i - 1]?.minPct ?? 33) : band.minPct}`;
        return { g: band.grade, r: rangeStr, p: band.desc, color: band.color };
      })
    : [
        { g: "A1", r: "91–100", p: "Outstanding",       color: gradeFor(95).color },
        { g: "A2", r: "81–90",  p: "Excellent",          color: gradeFor(85).color },
        { g: "B1", r: "71–80",  p: "Very Good",          color: gradeFor(75).color },
        { g: "B2", r: "61–70",  p: "Good",               color: gradeFor(65).color },
        { g: "C1", r: "51–60",  p: "Average",            color: gradeFor(55).color },
        { g: "C2", r: "41–50",  p: "Satisfactory",       color: gradeFor(45).color },
        { g: "D",  r: "33–40",  p: "Pass",               color: gradeFor(36).color },
        { g: "E",  r: "< 33",   p: "Needs Improvement",  color: gradeFor(20).color },
      ];

  return (
    <div style={{ display: "flex", gap: 0, border: "1.5px solid #aaa", borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
      <div style={{ background: theme, color: "#fff", fontWeight: 800, fontSize: 10, padding: "5px 10px", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
        GRADE SCALE
      </div>
      {bands.map(({ g, r, p, color }, i) => (
        <div key={i} style={{ flex: 1, textAlign: "center", padding: "4px 2px", borderLeft: "1px solid #ccc", background: i % 2 === 0 ? "#F8FAFC" : "#fff" }}>
          <div style={{ fontSize: 11, fontWeight: 900, color }}>{g}</div>
          <div style={{ fontSize: 9, color: "#222", fontWeight: 600 }}>{r}%</div>
          <div style={{ fontSize: 8.5, color: "#444", fontWeight: 500 }}>{p}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Colour utility ─────────────────────────────────── */
function darken(hex, pct) {
  try {
    const n = parseInt(hex.replace("#", ""), 16);
    const f = 1 - pct / 100;
    const r = Math.floor(((n >> 16) & 255) * f);
    const g = Math.floor(((n >> 8)  & 255) * f);
    const b = Math.floor(((n)       & 255) * f);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  } catch { return hex; }
}

/* ════════════════════════════════════════════════════════
   MINI CARD  (thumbnail — scaled-down A4 preview)
   ════════════════════════════════════════════════════════ */
function ModernMiniResultCard({ cols, tpl }) {
  const cfg    = parseCfg(tpl.layout_config);
  const theme  = cfg.themeColor  || "#0D7377";
  const accent = cfg.accentColor || "#F59E0B";
  const subjects   = Object.keys(SAMPLE_MARKS);
  const coscho     = (cfg.coScholasticActivities || DEFAULT_COSCHO).slice(0, 4);
  const showCoscho = !!cfg.hasCoScholastic;
  const showGraph  = !!cfg.showGraph;
  const marksAndGrade = cfg.coScholasticScale === "marks_and_grade";
  const SCALE = 0.34;
  const GRADE_COLORS = { A1: "#15803D", A2: "#16A34A", B1: "#0369A1", B2: "#2563EB", C1: "#D97706", C2: "#CA8A04", D: "#B91C1C", E: "#991B1B" };

  return (
    <div style={{ width: "270px", height: "254px", overflow: "hidden", position: "relative" }}>
      <div style={{ transformOrigin: "top left", transform: `scale(${SCALE})`, width: "794px" }}>

        {/* ── Modern gradient stripe ── */}
        <div style={{ height: 6, background: `linear-gradient(90deg, ${theme}, ${accent}, ${theme})` }} />

        {/* ── Modern white header ── */}
        <div style={{ display: "flex", alignItems: "center", padding: "10px 16px", background: "#fff", borderBottom: `1px solid #eee`, gap: 10 }}>
          <div style={{ width: 4, height: 48, background: theme, borderRadius: 2, flexShrink: 0 }} />
          <div style={{ width: 40, height: 40, borderRadius: 6, border: `2px solid ${theme}22`, background: `${theme}11`, flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: theme, letterSpacing: 0.5 }}>GREENFIELD PUBLIC SCHOOL</div>
            <div style={{ fontSize: 9, color: "#777" }}>Affiliated to CBSE | School Code: 98765</div>
            <div style={{ display: "inline-block", background: theme, color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 10px", borderRadius: 99, marginTop: 3 }}>
              {tpl.name.toUpperCase()} — 2024–25
            </div>
          </div>
        </div>

        {/* ── Modern student info cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, padding: "6px 10px", background: "#F8FAFC" }}>
          {[["Student", "Rohan Kumar"], ["Class", "VIII – A"], ["Roll No.", "14"], ["Adm. No.", "GPS/0814"]].map(([l, v], i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 4, border: `1px solid #eee`, borderLeft: `3px solid ${theme}`, padding: "4px 6px" }}>
              <div style={{ fontSize: 7, color: "#888", textTransform: "uppercase", letterSpacing: 0.3 }}>{l}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#1F2937" }}>{v}</div>
            </div>
          ))}
        </div>

        {/* ── Modern marks table ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
          <thead>
            <tr style={{ background: theme + "15" }}>
              <th style={{ ...mth, color: theme, border: "1px solid #e2e8f0", textAlign: "left", paddingLeft: 8 }}>Subject</th>
              <th style={{ ...mth, color: theme, border: "1px solid #e2e8f0" }}>MM</th>
              <th style={{ ...mth, color: theme, border: "1px solid #e2e8f0" }}>MO</th>
              <th style={{ ...mth, color: theme, border: "1px solid #e2e8f0" }}>%</th>
              <th style={{ ...mth, color: theme, border: "1px solid #e2e8f0" }}>Grade</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((sub, si) => {
              const mo = SAMPLE_MARKS[sub]?.[0] || 75;
              const pct = mo;
              const { g, color } = gradeFor(pct);
              return (
                <tr key={si} style={{ background: si % 2 === 0 ? "#fff" : "#F8FAFC" }}>
                  <td style={{ border: "1px solid #e2e8f0", padding: "3px 8px", textAlign: "left", fontWeight: 600, borderLeft: `3px solid ${color}` }}>{sub}</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "3px 4px", textAlign: "center" }}>100</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "3px 4px", textAlign: "center", fontWeight: 700 }}>{mo}</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "3px 4px", textAlign: "center" }}>{pct}%</td>
                  <td style={{ border: "1px solid #e2e8f0", padding: "3px 4px", textAlign: "center", fontWeight: 800, color }}>{g}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* ── Modern graph placeholder ── */}
        {showGraph && (
          <div style={{ padding: "6px 10px", background: "#F8FAFC", borderTop: "1px solid #eee" }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: theme, marginBottom: 3 }}>Subject-wise Performance</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {subjects.slice(0, 4).map((sub, si) => {
                const mo = SAMPLE_MARKS[sub]?.[0] || 75;
                const { color } = gradeFor(mo);
                return (
                  <div key={si} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 7 }}>
                    <div style={{ width: 55, textAlign: "right", color: "#555" }}>{sub}</div>
                    <div style={{ flex: 1, background: "#E5E7EB", borderRadius: 99, height: 7, position: "relative" }}>
                      <div style={{ width: `${mo}%`, height: "100%", background: color, borderRadius: 99 }} />
                    </div>
                    <div style={{ width: 24, fontWeight: 700, color }}>{mo}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Modern co-scholastic cards ── */}
        {showCoscho && (
          <div style={{ padding: "4px 10px", background: "#fff", borderTop: "1px solid #eee" }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: theme, marginBottom: 3 }}>CO-SCHOLASTIC ACTIVITIES</div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${coscho.length}, 1fr)`, gap: 4 }}>
              {coscho.map((a, i) => (
                <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 4, borderTop: `3px solid ${theme}`, padding: "4px 4px", textAlign: "center" }}>
                  <div style={{ fontSize: 7, color: "#666", marginBottom: 2 }}>{a}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: theme }}>{marksAndGrade ? "18/A" : COSCHO_GRADES[i]}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Modern summary metric cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, padding: "6px 10px", background: "#F8FAFC", borderTop: "1px solid #eee" }}>
          {[["Total", `${subjects.reduce((s, sub) => s + (SAMPLE_MARKS[sub]?.[0] || 0), 0)}/${subjects.length * 100}`, theme],
            ["Percentage", "79%", "#0369A1"],
            ["Grade", "B1", "#15803D"],
            ["Result", "PASS", "#15803D"]].map(([l, v, c], i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 4, border: "1px solid #eee", borderBottom: `3px solid ${c}`, padding: "4px 6px", textAlign: "center" }}>
              <div style={{ fontSize: 7, color: "#888", textTransform: "uppercase" }}>{l}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: c }}>{v}</div>
            </div>
          ))}
        </div>

        {/* ── Modern gradient footer stripe ── */}
        <div style={{ height: 5, background: `linear-gradient(90deg, ${theme}, ${accent}, ${theme})` }} />
      </div>
    </div>
  );
}

export function MiniResultCard({ cols, tpl }) {
  const cfg    = parseCfg(tpl.layout_config);
  if (cfg.layoutStyle === "modern") return <ModernMiniResultCard cols={cols} tpl={tpl} />;
  const theme  = cfg.themeColor  || "#1E3A5F";
  const accent = cfg.accentColor || "#F5C842";
  const defaultExamLabels = { 1: ["Exam"], 2: ["H.Y.", "Annual"], 3: ["Term 1", "Term 2", "Annual"], 4: ["PT-1", "HY", "PT-2", "Annual"] };
  const exams       = (cfg.examLabels || []).slice(0, cols).concat(defaultExamLabels[cols].slice((cfg.examLabels || []).length));
  const coscho      = cfg.coScholasticActivities || DEFAULT_COSCHO;
  const showCoscho  = !!cfg.hasCoScholastic;
  const showGraph   = !!cfg.showGraph;
  const marksAndGrade = cfg.coScholasticScale === "marks_and_grade";
  const subjects    = Object.keys(SAMPLE_MARKS);

  /* Scale A4 (794px wide) down to fit 270px container */
  const SCALE = 0.34;

  return (
    <div style={{ width: "270px", height: "254px", overflow: "hidden", position: "relative" }}>
      <div style={{ transformOrigin: "top left", transform: `scale(${SCALE})`, width: "794px" }}>

        {/* ── Mini Header ── */}
        <div style={{ background: theme, color: "#fff", display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: `3px solid ${accent}` }}>
          <div style={{ width: 36, height: 36, background: "rgba(255,255,255,0.1)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.3)" }} />
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 900, letterSpacing: 1 }}>GREENFIELD PUBLIC SCHOOL</div>
            <div style={{ fontSize: 9, opacity: 0.75 }}>Affiliated to CBSE | School Code: 98765</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: accent, marginTop: 4 }}>{tpl.name.toUpperCase()} — 2024–25</div>
          </div>
        </div>

        {/* ── Mini Student strip ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", fontSize: 9, padding: "4px 10px", background: theme + "12", borderBottom: "1px solid #ddd", gap: 4 }}>
          {[["Student", "Rohan Kumar"], ["Class", "VIII – A"], ["Roll No.", "14"], ["Adm. No.", "GPS/2024/0814"]].map(([l, v], i) => (
            <div key={i}><span style={{ color: "#666" }}>{l}: </span><strong>{v}</strong></div>
          ))}
        </div>

        {/* ── Mini Marks Table ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: cols === 4 ? 8 : 9 }}>
          <thead>
            <tr style={{ background: theme, color: "#fff" }}>
              <th style={mth}>Sno</th>
              <th style={{ ...mth, textAlign: "left" }}>Subject</th>
              {cols === 1 ? (
                <><th style={mth}>MM</th><th style={mth}>MO</th><th style={mth}>%</th><th style={mth}>Grd</th></>
              ) : (
                <>
                  {exams.map((e, i) => <th key={i} colSpan={2} style={mth}>{e}</th>)}
                  <th style={mth}>Total</th><th style={mth}>%</th><th style={mth}>Grd</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {subjects.map((sub, si) => {
              const m     = SAMPLE_MARKS[sub] || [75, 80, 72, 85];
              const total = exams.reduce((s, _, i) => s + m[i], 0);
              const pct   = Math.round(total / (exams.length * 100) * 100);
              const { g, color } = gradeFor(pct);
              const bg = si % 2 === 0 ? "#fff" : "#F7F9FC";
              return (
                <tr key={si} style={{ background: bg }}>
                  <td style={{ ...mtd, background: bg }}>{si + 1}</td>
                  <td style={{ ...mtd, textAlign: "left", background: bg, fontWeight: 600 }}>{sub}</td>
                  {cols === 1 ? (
                    <>
                      <td style={mtd}>100</td>
                      <td style={{ ...mtd, fontWeight: 700 }}>{m[0]}</td>
                      <td style={mtd}>{m[0]}%</td>
                      <td style={{ ...mtd, fontWeight: 700, color: gradeFor(m[0]).color }}>{gradeFor(m[0]).g}</td>
                    </>
                  ) : (
                    <>
                      {exams.map((_, i) => (
                        <React.Fragment key={i}>
                          <td style={{ ...mtd, fontWeight: 600 }}>{m[i]}</td>
                          <td style={{ ...mtd, color: gradeFor(Math.round(m[i])).color, fontWeight: 700 }}>{gradeFor(m[i]).g}</td>
                        </React.Fragment>
                      ))}
                      <td style={{ ...mtd, fontWeight: 800 }}>{total}</td>
                      <td style={mtd}>{pct}%</td>
                      <td style={{ ...mtd, fontWeight: 800, color }}>{g}</td>
                    </>
                  )}
                </tr>
              );
            })}
            <tr style={{ background: "#FFF3CD", fontWeight: 800 }}>
              <td colSpan={2} style={{ ...mtd, background: "#FFF3CD", textAlign: "right", paddingRight: 8 }}>TOTAL</td>
              {cols === 1 ? (
                <><td style={{ ...mtd, background: "#FFF3CD" }}>{subjects.length * 100}</td><td style={{ ...mtd, background: "#FFF3CD" }}>{subjects.reduce((s, sub) => s + (SAMPLE_MARKS[sub]?.[0] || 0), 0)}</td><td style={{ ...mtd, background: "#FFF3CD" }}>79%</td><td style={{ ...mtd, background: "#FFF3CD", color: "#15803D" }}>B1</td></>
              ) : (
                <>{exams.map((_, i) => <React.Fragment key={i}><td style={{ ...mtd, background: "#FFF3CD" }}>{subjects.reduce((s, sub) => s + (SAMPLE_MARKS[sub]?.[i] || 0), 0)}</td><td style={{ ...mtd, background: "#FFF3CD" }}>—</td></React.Fragment>)}<td style={{ ...mtd, background: "#FFF3CD" }}>{subjects.reduce((s, sub) => s + exams.reduce((ss, _, i) => ss + (SAMPLE_MARKS[sub]?.[i] || 0), 0), 0)}</td><td style={{ ...mtd, background: "#FFF3CD" }}>79%</td><td style={{ ...mtd, background: "#FFF3CD", color: "#15803D" }}>B1</td></>
              )}
            </tr>
          </tbody>
        </table>

        {/* ── Mini Graph ── */}
        {showGraph && cols > 1 && (
          <div style={{ padding: "6px 10px", borderTop: "1px solid #eee", background: "#FAFBFC" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme, marginBottom: 4 }}>Performance Graph</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 60 }}>
              {subjects.map((sub, si) => {
                const m = SAMPLE_MARKS[sub] || [75, 80, 72, 85];
                return (
                  <div key={si} style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 1, height: 55 }}>
                    {exams.map((_, i) => (
                      <div key={i} style={{ flex: 1, height: `${(m[i] / 100) * 55}px`, background: BAR_COLORS[i % 4], borderRadius: "2px 2px 0 0" }} />
                    ))}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              {exams.map((e, i) => <span key={i} style={{ fontSize: 8, display: "flex", alignItems: "center", gap: 3 }}><span style={{ width: 8, height: 8, background: BAR_COLORS[i % 4], display: "inline-block", borderRadius: 1 }} />{e}</span>)}
            </div>
          </div>
        )}

        {/* ── Mini Co-Scholastic ── */}
        {showCoscho && (
          <div style={{ borderTop: "1px solid #eee" }}>
            <div style={{ background: theme, color: accent, fontSize: 9, fontWeight: 800, padding: "3px 10px", letterSpacing: 0.5 }}>CO-SCHOLASTIC ACTIVITIES</div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(coscho.length + 1, 5)}, 1fr)`, textAlign: "center" }}>
              {[...coscho.slice(0, 4), "Attendance"].map((a, i) => (
                <div key={i} style={{ padding: "5px 4px", borderRight: "1px solid #eee", borderBottom: "1px solid #eee", background: i % 2 === 0 ? "#F7F9FC" : "#fff" }}>
                  <div style={{ fontSize: 8, color: "#666", marginBottom: 2 }}>{a}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: theme }}>
                    {a === "Attendance" ? "220/240" : marksAndGrade ? "18/A" : COSCHO_GRADES[i]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Mini Summary strip ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", background: theme, color: "#fff" }}>
          {[["Total", `${subjects.reduce((s, sub) => s + exams.reduce((ss, _, i) => ss + (SAMPLE_MARKS[sub]?.[i] || 0), 0), 0)} / ${subjects.length * cols * 100}`], ["Percentage", "79%"], ["Grade", "B1 — V. Good"], ["Result", "PASS"]].map(([l, v], i, arr) => (
            <div key={i} style={{ textAlign: "center", padding: "6px 4px", borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
              <div style={{ fontSize: 8, opacity: 0.75 }}>{l}</div>
              <div style={{ fontSize: v === "PASS" ? 13 : 11, fontWeight: 800, color: v === "PASS" ? "#4ADE80" : accent }}>{v}</div>
            </div>
          ))}
        </div>

        {/* ── Mini Signatures ── */}
        <div style={{ display: "flex", justifyContent: "space-around", padding: "10px 12px 8px", borderTop: `2px solid ${theme}` }}>
          {["Class Teacher", "Principal", "Parent/Guardian"].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ width: 80, height: 1, background: "#999", marginBottom: 4 }} />
              <div style={{ fontSize: 8, color: "#555" }}>{s}</div>
            </div>
          ))}
        </div>

        {/* ── Mini Footer ── */}
        <div style={{ borderTop: "1px solid #ddd", padding: "3px 10px", textAlign: "center", fontSize: 7.5, color: "#999", fontFamily: "Arial, sans-serif" }}>
          Volcanic Result Management System | Volcanic Education &amp; Technology Pvt Ltd
        </div>
      </div>
    </div>
  );
}

/* ── Mini table cell styles ─────────────────────────── */
const mth = { border: "1px solid rgba(255,255,255,0.2)", padding: "4px 3px", textAlign: "center", fontWeight: 700 };
const mtd = { border: "1px solid #ddd", padding: "3px 4px", textAlign: "center" };

export default MiniResultCard;
